import type { TaskQueueOutcome } from '../../utils/async';

export const DEFAULT_RECEIPT_UPLOAD_CONCURRENCY = 5;
export const DEFAULT_RECEIPT_UPLOAD_MAX_FILES_PER_BATCH = 100;
/**
 * Upper bound on a single receipt's storage upload, covering every `retryAsync` attempt. The
 * request itself has no timeout, so without this a stalled upload would hold its queue slot and
 * its exemption from the local expiry deadline for the rest of the session.
 */
export const DEFAULT_RECEIPT_UPLOAD_REQUEST_TIMEOUT_MS = 5 * 60 * 1000;

export type ReceiptUploadBatch<T> = {
  accepted: ReadonlyArray<T>;
  excess: ReadonlyArray<T>;
};

export type ReceiptUploadFile = {
  fileUri: string;
  fileName: string;
  mimeType: string;
};

export type QueuedReceiptUpload = ReceiptUploadFile & {
  localId: string;
  createdAt: number;
  localUploadOrder: {
    batchCreatedAt: number;
    batchIndex: number;
  };
};

/** A queued upload either ran to completion or was dropped before it started. */
export type ReceiptUploadOutcome = TaskQueueOutcome;

const assertPositiveInteger = (value: number, name: string): void => {
  if (!Number.isInteger(value) || value < 1) {
    throw new Error(`${name} must be a positive integer.`);
  }
};

export class ReceiptUploadTimeoutError extends Error {
  constructor(timeoutMs: number) {
    super(`Receipt upload did not finish within ${timeoutMs}ms.`);
    this.name = 'ReceiptUploadTimeoutError';
  }
}

export type ReceiptUploadDeadlineOptions = {
  timeoutMs?: number;
  onTimeout?: () => void;
};

/** Rejects when `work` outlives the deadline and aborts it when the caller provides a callback. */
export const withReceiptUploadDeadline = async <T>(
  work: Promise<T>,
  options: number | ReceiptUploadDeadlineOptions = DEFAULT_RECEIPT_UPLOAD_REQUEST_TIMEOUT_MS,
): Promise<T> => {
  const { timeoutMs, onTimeout } = typeof options === 'number' ? { timeoutMs: options, onTimeout: undefined } : options;
  const effectiveTimeoutMs = timeoutMs ?? DEFAULT_RECEIPT_UPLOAD_REQUEST_TIMEOUT_MS;
  assertPositiveInteger(effectiveTimeoutMs, 'timeoutMs');
  let timer: ReturnType<typeof setTimeout> | undefined;

  try {
    return await Promise.race([
      work,
      new Promise<never>((_resolve, reject) => {
        timer = setTimeout(() => {
          onTimeout?.();
          reject(new ReceiptUploadTimeoutError(effectiveTimeoutMs));
        }, effectiveTimeoutMs);
      }),
    ]);
  } finally {
    clearTimeout(timer);
  }
};

export const splitReceiptUploadBatch = <T>(
  files: ReadonlyArray<T>,
  maxFiles: number = DEFAULT_RECEIPT_UPLOAD_MAX_FILES_PER_BATCH,
): ReceiptUploadBatch<T> => {
  assertPositiveInteger(maxFiles, 'maxFiles');
  return {
    accepted: files.slice(0, maxFiles),
    excess: files.slice(maxFiles),
  };
};

export const createQueuedReceiptUploads = (
  files: ReadonlyArray<ReceiptUploadFile>,
  createLocalId: () => string,
  now: () => number = Date.now,
): ReadonlyArray<QueuedReceiptUpload> => {
  const createdAt = now();
  return files.map((file, batchIndex) => ({
    ...file,
    localId: createLocalId(),
    createdAt,
    localUploadOrder: { batchCreatedAt: createdAt, batchIndex },
  }));
};
